<template>
  <div class="p-4 md:p-6 lg:p-8 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.push('/inicio')"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-custom-primary dark:hover:text-custom-primary transition-colors border-0 bg-transparent"
      >
        <i class="bx bx-arrow-back text-lg"></i>
        Regresar
      </button>
      <div class="h-5 w-px bg-slate-300 dark:bg-slate-600"></div>
      <div>
        <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">
          Corte de Ventas — {{ brand.nombre }}
        </h1>
        <p class="text-xs text-slate-500 dark:text-slate-400">Hospedaje + Restaurante agrupado por empleado que registró el cobro</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Desde</label>
          <input type="date" v-model="filtro.fecha_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hasta</label>
          <input type="date" v-model="filtro.fecha_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora inicio</label>
          <input type="time" v-model="filtro.hora_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hora fin</label>
          <input type="time" v-model="filtro.hora_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Empleado</label>
          <select v-model="filtro.empleado_id" class="form-control form-control-sm">
            <option value="">Todos</option>
            <option v-for="e in empleados" :key="e.id" :value="e.id">{{ e.name }}</option>
          </select>
        </div>
        <button @click="cargar" class="btn btn-outline-secondary btn-sm">
          <i class="bx bx-search mr-1"></i> Filtrar
        </button>
        <button @click="exportarPDF" class="btn btn-outline-primary btn-sm">
          <i class="bx bx-file-pdf mr-1"></i> PDF
        </button>
        <button @click="exportarExcel" class="btn btn-outline-success btn-sm">
          <i class="bx bx-spreadsheet mr-1"></i> Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-10">
      <i class="bx bx-loader-alt bx-spin text-3xl text-custom-primary"></i>
    </div>

    <div v-else>
      <div v-if="!bloques.length" class="bg-white dark:bg-slate-800 rounded-xl p-10 text-center text-slate-500 dark:text-slate-400 shadow-sm">
        No hay cobros registrados para el período seleccionado.
      </div>

      <div v-for="bloque in bloques" :key="bloque.empleado.id ?? 'sin-registrar'" class="bg-white dark:bg-slate-800 rounded-xl shadow-sm mb-6 overflow-hidden">
        <div class="bg-slate-800 dark:bg-slate-900 text-white px-4 py-3">
          <h2 class="text-base font-bold">{{ bloque.empleado.nombre }}</h2>
        </div>

        <div class="p-4">
          <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-2">Ingreso de Hotel</p>
          <div class="overflow-x-auto mb-4">
            <table class="w-full text-xs">
              <thead>
                <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <th class="px-2 py-2 text-left">Hab.</th>
                  <th class="px-2 py-2 text-left">Huésped</th>
                  <th class="px-2 py-2 text-left">Ingreso</th>
                  <th class="px-2 py-2 text-left">Salida</th>
                  <th class="px-2 py-2 text-left">Fac. Hotel</th>
                  <th class="px-2 py-2 text-left">POS</th>
                  <th class="px-2 py-2 text-right">Efectivo</th>
                  <th class="px-2 py-2 text-right">Voucher</th>
                  <th class="px-2 py-2 text-right">Depósito</th>
                  <th class="px-2 py-2 text-right">Fact. por Cobrar</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!bloque.hotel.length"><td colspan="10" class="text-center py-3 text-slate-400">Sin cobros de hotel</td></tr>
                <tr v-for="(f, i) in bloque.hotel" :key="'h'+i" class="border-b border-slate-100 dark:border-slate-700">
                  <td class="px-2 py-1.5">{{ f.habitacion }}</td>
                  <td class="px-2 py-1.5">{{ f.huesped }}</td>
                  <td class="px-2 py-1.5">{{ formatFecha(f.ingreso) }}</td>
                  <td class="px-2 py-1.5">{{ formatFecha(f.salida) }}</td>
                  <td class="px-2 py-1.5">{{ f.fac_hotel || '—' }}</td>
                  <td class="px-2 py-1.5">{{ f.pos || '—' }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.efectivo) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.voucher) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.deposito) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.por_cobrar) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="bloque.hotel.length">
                <tr class="bg-slate-100 dark:bg-slate-900 font-semibold">
                  <td colspan="6" class="px-2 py-2">TOTAL HOTEL</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.hotel.efectivo) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.hotel.voucher) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.hotel.deposito) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.hotel.por_cobrar) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p class="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Ingreso de Restaurante</p>
          <div class="overflow-x-auto mb-4">
            <table class="w-full text-xs">
              <thead>
                <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <th class="px-2 py-2 text-left">Referencia</th>
                  <th class="px-2 py-2 text-left">Fac. Rest.</th>
                  <th class="px-2 py-2 text-left">POS</th>
                  <th class="px-2 py-2 text-right">Efectivo</th>
                  <th class="px-2 py-2 text-right">Voucher</th>
                  <th class="px-2 py-2 text-right">Depósito</th>
                  <th class="px-2 py-2 text-right">Fact. por Cobrar</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!bloque.restaurante.length"><td colspan="7" class="text-center py-3 text-slate-400">Sin cobros de restaurante</td></tr>
                <tr v-for="(f, i) in bloque.restaurante" :key="'r'+i" class="border-b border-slate-100 dark:border-slate-700">
                  <td class="px-2 py-1.5">{{ f.referencia }}</td>
                  <td class="px-2 py-1.5">{{ f.fac_rest || '—' }}</td>
                  <td class="px-2 py-1.5">{{ f.pos || '—' }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.efectivo) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.voucher) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.deposito) }}</td>
                  <td class="px-2 py-1.5 text-right font-mono">{{ fmt(f.por_cobrar) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="bloque.restaurante.length">
                <tr class="bg-slate-100 dark:bg-slate-900 font-semibold">
                  <td colspan="3" class="px-2 py-2">TOTAL RESTAURANTE</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.restaurante.efectivo) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.restaurante.voucher) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.restaurante.deposito) }}</td>
                  <td class="px-2 py-2 text-right font-mono">{{ fmt(bloque.totales.restaurante.por_cobrar) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="flex justify-end">
            <div class="bg-custom-primary/10 dark:bg-custom-primary/20 rounded-lg p-4 w-full max-w-sm">
              <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-2">Resumen</p>
              <div class="flex justify-between text-sm py-0.5"><span>Efectivo</span><span class="font-mono">Q{{ fmt(bloque.resumen.efectivo) }}</span></div>
              <div class="flex justify-between text-sm py-0.5"><span>Depósito</span><span class="font-mono">Q{{ fmt(bloque.resumen.deposito) }}</span></div>
              <div class="flex justify-between text-sm py-0.5"><span>Pend. de Pago</span><span class="font-mono">Q{{ fmt(bloque.resumen.pend_pago) }}</span></div>
              <div class="flex justify-between text-sm py-0.5"><span>Voucher</span><span class="font-mono">Q{{ fmt(bloque.resumen.voucher) }}</span></div>
              <div class="flex justify-between text-base font-bold text-custom-primary border-t border-slate-300 dark:border-slate-600 mt-1 pt-1">
                <span>TOTAL</span><span class="font-mono">Q{{ fmt(bloque.resumen.total) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gran total -->
      <div v-if="bloques.length" class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-5">
        <p class="text-sm font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider mb-3">Gran Total</p>
        <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <p class="text-xs text-green-600 dark:text-green-400">Efectivo</p>
            <p class="text-base font-bold text-green-700 dark:text-green-300">Q{{ fmt(granTotal.efectivo) }}</p>
          </div>
          <div class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <p class="text-xs text-purple-600 dark:text-purple-400">Depósito</p>
            <p class="text-base font-bold text-purple-700 dark:text-purple-300">Q{{ fmt(granTotal.deposito) }}</p>
          </div>
          <div class="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3">
            <p class="text-xs text-violet-600 dark:text-violet-400">Pend. de Pago</p>
            <p class="text-base font-bold text-violet-700 dark:text-violet-300">Q{{ fmt(granTotal.por_cobrar) }}</p>
          </div>
          <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <p class="text-xs text-blue-600 dark:text-blue-400">Voucher</p>
            <p class="text-base font-bold text-blue-700 dark:text-blue-300">Q{{ fmt(granTotal.voucher) }}</p>
          </div>
          <div class="bg-custom-primary/10 dark:bg-custom-primary/20 rounded-lg p-3">
            <p class="text-xs text-custom-primary">Total General</p>
            <p class="text-base font-bold text-custom-primary">Q{{ fmt(granTotal.total) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';
import { BRAND } from '@/config/branding';

export default {
  name: 'CorteVentas',
  data() {
    const hoy = new Date().toISOString().substring(0, 10);
    return {
      brand: BRAND,
      loading: false,
      bloques: [],
      granTotal: { efectivo: 0, deposito: 0, por_cobrar: 0, voucher: 0, total: 0 },
      empleados: [],
      filtro: {
        fecha_inicio: hoy,
        fecha_fin: hoy,
        hora_inicio: '08:00',
        hora_fin: '08:00',
        empleado_id: '',
      },
    };
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    formatFecha(f) {
      if (!f) return '—';
      const d = new Date(String(f).substring(0, 10) + 'T12:00:00');
      return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    buildParams() {
      const params = {
        fecha_inicio: this.filtro.fecha_inicio,
        fecha_fin: this.filtro.fecha_fin || this.filtro.fecha_inicio,
        hora_inicio: this.filtro.hora_inicio,
        hora_fin: this.filtro.hora_fin,
      };
      if (this.filtro.empleado_id) {
        params.empleado_id = this.filtro.empleado_id;
      }
      return params;
    },
    async cargarEmpleados() {
      try {
        const { data } = await axios.get(`${API_URL}/cierre-caja-recepcionistas`);
        this.empleados = data;
      } catch (e) {
        console.error(e);
      }
    },
    async cargar() {
      try {
        this.loading = true;
        const { data } = await axios.get(`${API_URL}/corte-ventas`, { params: this.buildParams() });
        this.bloques = data.bloques;
        this.granTotal = data.gran_total;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async exportarPDF() {
      try {
        const response = await axios.get(`${API_URL}/corte-ventas/pdf`, {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `corte-ventas-${this.filtro.fecha_inicio}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const json = JSON.parse(text);
            alert(json.error || json.message || 'Error al generar el PDF.');
          } catch { alert('Error al generar el PDF.'); }
        } else {
          console.error(e);
          alert('Error al generar el PDF.');
        }
      }
    },
    async exportarExcel() {
      try {
        const response = await axios.get(`${API_URL}/corte-ventas/excel`, {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `corte-ventas-${this.filtro.fecha_inicio}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const json = JSON.parse(text);
            alert(json.error || json.message || 'Error al generar el Excel.');
          } catch { alert('Error al generar el Excel.'); }
        } else {
          console.error(e);
          alert('Error al generar el Excel.');
        }
      }
    },
  },
  mounted() {
    this.cargarEmpleados();
    this.cargar();
  },
};
</script>
